import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField as MuiTextField,
  Tooltip,
  Divider,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import api from '../../services/api';

const ConstructionsList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState({});
  const [allScripts, setAllScripts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [allCompanies, setAllCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [openRoles, setOpenRoles] = useState(false);
  const [editingRolesItem, setEditingRolesItem] = useState(null);
  const [rolesFormData, setRolesFormData] = useState({});
  const [openDetails, setOpenDetails] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);
  const [openCompany, setOpenCompany] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyFormData, setCompanyFormData] = useState({});
  const [invoiceOnFormData, setInvoiceOnFormData] = useState({});

  const groupedData = useMemo(() => {
    return data.reduce((acc, item) => {
      const companyId = item.company;
      const companyTitle = item.company_title || item.company;
      if (!acc[companyId]) acc[companyId] = { title: companyTitle, constructions: [] };
      acc[companyId].constructions.push(item);
      return acc;
    }, {});
  }, [data]);

  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    const newExpanded = {};
    Object.keys(groupedData).forEach(companyId => newExpanded[companyId] = true);
    setExpandedRows(newExpanded);
  }, [groupedData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/api/constructions/');
        const data = response.data.results || response.data;
        setData(Array.isArray(data) ? data : []);

        // Загрузка деталей для всех объектов
        const detailsPromises = data.map(item =>
          api.get(`/api/constructions/${item.id}/`)
            .then(response => ({ id: item.id, data: response.data }))
            .catch(err => {
              console.error('Ошибка загрузки деталей для', item.id, err);
              return null;
            })
        );
        const detailsResults = await Promise.all(detailsPromises);
        detailsResults.forEach(result => {
          if (result) {
            setDetails(prev => ({ ...prev, [result.id]: result.data }));
          }
        });
      } catch (err) {
        setError('Ошибка загрузки данных');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchScripts = async () => {
      try {
        const response = await api.get('/api/scripts/');
        setAllScripts(response.data.results || response.data);
      } catch (err) {
        console.error('Ошибка загрузки скриптов:', err);
      }
    };

    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await api.get('/api/users/');
        setAllUsers(response.data.results || response.data);
      } catch (err) {
        console.error('Ошибка загрузки пользователей:', err);
        setAllUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    const fetchCompanies = async () => {
      try {
        const response = await api.get('/api/companies/');
        setAllCompanies(response.data.results || response.data);
      } catch (err) {
        console.error('Ошибка загрузки компаний:', err);
      }
    };

    fetchData();
    fetchScripts();
    fetchUsers();
    fetchCompanies();
  }, []);


  const handleAdd = (companyId) => {
    setEditingItem(null);
    setFormData({ company: companyId, scripts: [] });
    setOpen(true);
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    let currentDetails = details[item.id];
    if (!currentDetails) {
      try {
        const response = await api.get(`/api/constructions/${item.id}/`);
        currentDetails = response.data;
        setDetails(prev => ({ ...prev, [item.id]: currentDetails }));
      } catch (err) {
        console.error('Ошибка загрузки деталей:', err);
        currentDetails = null;
      }
    }
    const scripts = currentDetails && currentDetails.scripts
      ? currentDetails.scripts.map(s => allScripts.find(sc => sc.id === s.id)).filter(Boolean)
      : [];
    setFormData({ ...item, scripts });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const processedData = {
        ...formData,
        scripts: (formData.scripts || []).filter(s => s && s.id).map(s => s.id),
      };
      if (editingItem) {
        const response = await api.put(`/api/constructions/${editingItem.id}/`, processedData);
        setData(prev => prev.map(i => i.id === editingItem.id ? response.data : i));
        const detailResponse = await api.get(`/api/constructions/${editingItem.id}/`);
        setDetails(prev => ({ ...prev, [editingItem.id]: detailResponse.data }));
      } else {
        const response = await api.post('/api/constructions/', processedData);
        setData(prev => [...prev, response.data]);
      }
      setOpen(false);
    } catch (err) {
      console.error('Ошибка сохранения:', err);
    }
  };

  const handleEditRoles = async (item) => {
    setEditingRolesItem(item);
    let currentDetails = details[item.id];
    if (!currentDetails) {
      try {
        const response = await api.get(`/api/constructions/${item.id}/`);
        currentDetails = response.data;
        setDetails(prev => ({ ...prev, [item.id]: currentDetails }));
      } catch (err) {
        console.error('Ошибка загрузки деталей:', err);
        currentDetails = null;
      }
    }
    const rolesData = {};
    const invoiceOnData = {};
    if (currentDetails && currentDetails.users) {
      currentDetails.users.forEach(user => {
        user.roles_display.forEach(role => {
          const isMultiple = role.name === 'Мастер' || role.name === 'Оплата';
          if (isMultiple) {
            if (!rolesData[role.name]) rolesData[role.name] = [];
            rolesData[role.name].push(user.user);
          } else {
            rolesData[role.name] = user.user;
          }
          if (role.name === 'Оплата') {
            invoiceOnData[user.user] = user.invoice_on || 'Радонеж';
          }
        });
      });
    }
    setRolesFormData(rolesData);
    setInvoiceOnFormData(invoiceOnData);
    setOpenRoles(true);
  };

  const handleOpenDetails = async (item) => {
    setDetailsItem(item);
    if (!details[item.id]) {
      try {
        const response = await api.get(`/api/constructions/${item.id}/`);
        setDetails(prev => ({ ...prev, [item.id]: response.data }));
      } catch (err) {
        console.error('Ошибка загрузки деталей:', err);
      }
    }
    setOpenDetails(true);
  };

  const handleSaveRoles = async () => {
    const users = [];
    const userMap = {};
    Object.entries(rolesFormData).forEach(([roleName, userIds]) => {
      const ids = Array.isArray(userIds) ? userIds : [userIds];
      ids.forEach(userId => {
        if (userId) {
          if (!userMap[userId]) {
            const user = allUsers.find(u => u.id === userId);
            if (user) {
              userMap[userId] = { ...user, roles_display: [] };
              users.push(userMap[userId]);
            }
          }
          if (userMap[userId]) {
            userMap[userId].roles_display.push({ name: roleName });
          }
        }
      });
    });
    // Добавить invoice_on для пользователей с ролью "Оплата"
    users.forEach(user => {
      if (user.roles_display.some(role => role.name === 'Оплата')) {
        user.invoice_on = invoiceOnFormData[user.id] || 'Радонеж';
      }
    });
    try {
      const response = await api.patch(`/api/constructions/${editingRolesItem.id}/update_users/`, { users });
      setDetails(prev => ({ ...prev, [editingRolesItem.id]: response.data }));
      setOpenRoles(false);
    } catch (err) {
      console.error('Ошибка сохранения ролей:', err);
    }
  };

  const handleAddCompany = () => {
    setEditingCompany(null);
    setCompanyFormData({});
    setOpenCompany(true);
  };

  const handleSaveCompany = async () => {
    try {
      if (editingCompany) {
        await api.put(`/api/companies/${editingCompany.id}/`, companyFormData);
      } else {
        await api.post('/api/companies/', companyFormData);
      }
      setOpenCompany(false);
      // Optionally refresh data
    } catch (err) {
      console.error('Ошибка сохранения компании:', err);
    }
  };

  const getProblems = (item) => {
    const detail = details[item.id];
    if (!detail) return 'Загрузка...';
    const problems = [];
    if (detail.users) {
      const assignedRoles = new Set();
      detail.users.forEach(user => {
        user.roles_display.forEach(role => assignedRoles.add(role.name));
      });
      const allRoles = ['Мастер', 'ПТО', 'Снабженец', 'Сметчик', 'Руководитель проекта', 'Бухгалтер', 'Директор', 'Юрист', 'Оплата'];
      const missing = allRoles.filter(role => !assignedRoles.has(role));
      if (missing.length > 0) {
        problems.push(`Отсутствуют роли: ${missing.join(', ')}`);
      }
    }
    if (!detail.scripts || detail.scripts.length === 0) {
      problems.push('Нет скриптов');
    }
    if (problems.length === 0) return 'Нет проблем';
    return problems.join('\n');
  };

  if (loading) return <Typography>Загрузка...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Button variant="contained" onClick={handleAddCompany}>Создать компанию</Button>
      </Box>
      <TableContainer component={Paper}>
      <Table>
        <TableBody>
          {allCompanies.map((company) => {
            const constructions = data.filter(item => item.company === company.id);
            return (
              <React.Fragment key={company.id}>
                <TableRow
                  onClick={() => setExpandedRows(prev => ({ ...prev, [company.id]: !prev[company.id] }))}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell colSpan={7}>
                    <IconButton
                      aria-label="expand company"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setExpandedRows(prev => ({ ...prev, [company.id]: !prev[company.id] })) }}
                    >
                      {expandedRows[company.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                    <Typography variant="h6" component="span" sx={{ ml: 1 }}>{company.title}</Typography>
                    <Button variant="outlined" size="small" sx={{ ml: 2 }} onClick={(e) => { e.stopPropagation(); handleAdd(company.id); }}>Добавить объект</Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={expandedRows[company.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        {constructions.length > 0 ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Название</TableCell>
                                <TableCell>Главный чат в компании</TableCell>
                                <TableCell>Проблемы</TableCell>
                                <TableCell>Действия</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {constructions.map((item) => (
                                <TableRow key={item.id} onClick={() => handleOpenDetails(item)} sx={{ cursor: 'pointer' }}>
                                  <TableCell>{item.title}</TableCell>
                                  <TableCell>{item.main_in_company ? 'Да' : 'Нет'}</TableCell>
                                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', color: getProblems(item) === 'Нет проблем' ? 'green' : 'red', whiteSpace: 'pre-line' }} title={getProblems(item)}>{getProblems(item)}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                      <Button
                                        variant="text"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                                      >
                                        Редактировать
                                      </Button>
                                      <Button
                                        variant="text"
                                        size="small"
                                        startIcon={<GroupIcon />}
                                        onClick={(e) => { e.stopPropagation(); handleEditRoles(item); }}
                                      >
                                        Изменить роли
                                      </Button>
                                      {/* <Button
                                        variant="text"
                                        size="small"
                                        startIcon={<InfoIcon />}
                                        onClick={(e) => { e.stopPropagation(); handleOpenDetails(item); }}
                                      >
                                        Суммарная информация
                                      </Button> */}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography sx={{ p: 2 }}>Нет объектов</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Редактировать объект' : 'Добавить объект'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название"
            fullWidth
            variant="standard"
            value={formData.title || ''}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Адрес"
            fullWidth
            variant="standard"
            value={formData.address || ''}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Chat ID"
            fullWidth
            variant="standard"
            value={formData.chat_id || ''}
            onChange={(e) => setFormData({ ...formData, chat_id: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.main_in_company || false}
                onChange={(e) => setFormData({ ...formData, main_in_company: e.target.checked })}
              />
            }
            label="Главный в компании"
          />
          <Typography variant="h6" sx={{ mt: 2 }}>Скрипты:</Typography>
          {allScripts.map((script) => {
            const steps = script.steps || [];
            const tooltipTitle = steps.length > 0 ? (
              <div>
                {steps.map((step, index) => (
                  <div key={index}>{step.sequence}. {step.role_name || step.role}</div>
                ))}
              </div>
            ) : 'Нет шагов';
            return (
              <Box key={script.id} sx={{ mb: 1 }}>
                <Tooltip title={tooltipTitle} arrow>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(formData.scripts || []).some((s) => s.id === script.id)}
                        onChange={(e) => {
                          const scripts = e.target.checked
                            ? [...(formData.scripts || []), script]
                            : (formData.scripts || []).filter((s) => s.id !== script.id);
                          setFormData({ ...formData, scripts });
                        }}
                      />
                    }
                    label={`${script.title} (ID: ${script.id})`}
                  />
                </Tooltip>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button onClick={handleSave}>Сохранить</Button>
        </DialogActions>
      </Dialog>
        <Dialog open={openRoles} onClose={() => setOpenRoles(false)} maxWidth="md" fullWidth>
           <DialogTitle>Изменить роли</DialogTitle>
           <DialogContent>
             {loadingUsers ? (
               <Typography>Загрузка пользователей...</Typography>
             ) : (
             [
               'Мастер',
               'ПТО',
               'Снабженец',
               'Сметчик',
               'Руководитель проекта',
               'Бухгалтер',
               'Директор',
               'Юрист',
               'Оплата'
             ].map((roleName) => {
               const isMultiple = roleName === 'Мастер' || roleName === 'Оплата';
               return (
                 <Box key={roleName} sx={{ mb: 2 }}>
                   <Typography variant="h6">{roleName}:</Typography>
                   {isMultiple ? (
                     <Autocomplete
                       multiple
                       options={allUsers}
                       getOptionLabel={(option) => `${option.telegram_id ? `(${option.telegram_id}) ` : ''}${option.username || option.telegram_username || ''}`}
                       value={allUsers.filter(u => (rolesFormData[roleName] || []).includes(u.id))}
                       onChange={(event, newValue) => {
                         const newIds = newValue.map(u => u.id);
                         setRolesFormData({...rolesFormData, [roleName]: newIds});
                         if (roleName === 'Оплата') {
                           const currentIds = rolesFormData[roleName] || [];
                           const added = newIds.filter(id => !currentIds.includes(id));
                           const removed = currentIds.filter(id => !newIds.includes(id));
                           let newInvoiceOn = { ...invoiceOnFormData };
                           added.forEach(id => {
                             if (!newInvoiceOn[id]) newInvoiceOn[id] = 'Радонеж';
                           });
                           removed.forEach(id => {
                             delete newInvoiceOn[id];
                           });
                           setInvoiceOnFormData(newInvoiceOn);
                         }
                       }}
                       renderInput={(params) => <MuiTextField {...params} label={roleName} />}
                     />
                   ) : (
                     <FormControl fullWidth sx={{ mt: 1 }}>
                       <InputLabel>{roleName}</InputLabel>
                       <Select
                         value={rolesFormData[roleName] || ''}
                         onChange={(e) =>
                           setRolesFormData({
                             ...rolesFormData,
                             [roleName]: e.target.value,
                           })
                         }
                       >
                         <MenuItem value="">
                           <em>Никого</em>
                         </MenuItem>
                         {allUsers.map((user) => (
                           <MenuItem key={user.id} value={user.id}>
                             {`${user.telegram_id ? `(${user.telegram_id}) ` : ''}${user.username || user.telegram_username || ''}`}
                           </MenuItem>
                         ))}
                       </Select>
                     </FormControl>
                   )}
                 </Box>
               );
             })
             )}
             {rolesFormData['Оплата'] && rolesFormData['Оплата'].length > 0 && (
               <Box sx={{ mt: 3 }}>
                 <Typography variant="h6">Редактирование счета для роли "Оплата":</Typography>
                 {rolesFormData['Оплата'].map(userId => {
                   const user = allUsers.find(u => u.id === userId);
                   return (
                     <Box key={userId} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                       <Typography sx={{ mr: 2, minWidth: 200 }}>
                         {user ? `${user.telegram_id ? `(${user.telegram_id}) ` : ''}${user.username || user.telegram_username || ''}` : `Пользователь ${userId}`}
                       </Typography>
                       <TextField
                         label="Счет на"
                         value={invoiceOnFormData[userId] || ''}
                         onChange={(e) => setInvoiceOnFormData({ ...invoiceOnFormData, [userId]: e.target.value })}
                         fullWidth
                       />
                     </Box>
                   );
                 })}
               </Box>
             )}
           </DialogContent>
           <DialogActions>
            <Button onClick={() => setOpenRoles(false)}>Отмена</Button>
            <Button onClick={handleSaveRoles}>Сохранить</Button>
           </DialogActions>
        </Dialog>
        <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
          <DialogTitle>Детали объекта: {detailsItem?.title}</DialogTitle>
          <DialogContent>
            {details[detailsItem?.id] ? (
              <Box>
                <Typography><strong>Сценарии:</strong></Typography>
                {details[detailsItem.id].scripts && details[detailsItem.id].scripts.length > 0 ? (
                  details[detailsItem.id].scripts.map(script => {
                    const fullScript = allScripts.find(s => s.id === script.id);
                    const steps = fullScript ? (fullScript.steps || []).sort((a, b) => a.sequence - b.sequence) : [];
                    const tooltipTitle = steps.length > 0 ? (
                      <div>
                        {steps.map((step, index) => (
                          <div key={index}>{step.sequence}. {step.role_name || step.role}</div>
                        ))}
                      </div>
                    ) : 'Нет шагов';
                    return (
                      <Tooltip key={script.id} title={tooltipTitle} arrow>
                        <Chip label={script.title} sx={{ m: 0.5 }} />
                      </Tooltip>
                    );
                  })
                ) : (
                  <Typography>Нет сценариев</Typography>
                )}
                <Divider sx={{ my: 2 }} />
                {(() => {
                  const roleOrder = [
                    'Мастер',
                    'ПТО',
                    'Снабженец',
                    'Сметчик',
                    'Руководитель проекта',
                    'Бухгалтер',
                    'Директор',
                    'Юрист',
                    'Оплата'
                  ];

                  const usersByRole = {};
                  if (details[detailsItem.id].users) {
                    details[detailsItem.id].users.forEach(user => {
                      user.roles_display.forEach(role => {
                        if (!usersByRole[role.name]) {
                          usersByRole[role.name] = [];
                        }
                        usersByRole[role.name].push(user);
                      });
                    });
                  }

                  return roleOrder.map(roleName => (
                    <Box key={roleName} sx={{ mb: 1 }}>
                      <Typography><strong>{roleName}:</strong></Typography>
                      {usersByRole[roleName] && usersByRole[roleName].length > 0 ? (
                        usersByRole[roleName].map(user => (
                          <Box key={user.id} sx={{ ml: 2, mb: 0.5 }}>
                            <Typography>
                              ({user.telegram_id}) {user.username}
                              {roleName === 'Оплата' && ` (Счет на: ${user.invoice_on})`}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography sx={{ ml: 2, color: 'text.secondary' }}>Никого нет</Typography>
                      )}
                    </Box>
                  ));
                })()}
              </Box>
            ) : (
              <Typography>Загрузка деталей...</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetails(false)}>Закрыть</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={openCompany} onClose={() => setOpenCompany(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingCompany ? 'Редактировать компанию' : 'Создать компанию'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название"
              fullWidth
              variant="standard"
              value={companyFormData.title || ''}
              onChange={(e) => setCompanyFormData({ ...companyFormData, title: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCompany(false)}>Отмена</Button>
            <Button onClick={handleSaveCompany}>Сохранить</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

export default ConstructionsList;
